import React, { useId } from "react";
import { cn } from "@/lib/utils";

/**
 * Family Souq — Maison crest monogram (interlocked F + S inside a hexagonal
 * arabesque frame) rendered as inline SVG. No external assets.
 */
export const CrestMark = ({ size = 32, variant = "brass", className, "data-testid": testId, ...rest }) => {
  const raw = useId();
  const id = raw.replace(/[:]/g, "");
  const s1 = variant === "ivory" ? "#E7D7BF" : "#B08D57";
  const s2 = variant === "ivory" ? "#F6F1E7" : "#D7C09A";
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      role="img"
      aria-label="Family Souq crest"
      data-testid={testId || "crest-mark"}
      className={cn(
        "shrink-0 transition-transform duration-300 ease-out will-change-transform group-hover:rotate-[1.5deg] motion-reduce:transform-none",
        className
      )}
      {...rest}
    >
      <defs>
        <linearGradient id={`crest-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={s1} />
          <stop offset="1" stopColor={s2} />
        </linearGradient>
      </defs>
      {/* outer hexagon frame */}
      <path
        d="M32 3 L57 17.5 L57 46.5 L32 61 L7 46.5 L7 17.5 Z"
        stroke={`url(#crest-${id})`}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      {/* inset engraving hexagon */}
      <path
        d="M32 9 L51.5 20.25 L51.5 43.75 L32 55 L12.5 43.75 L12.5 20.25 Z"
        stroke="rgba(231,215,191,0.26)"
        strokeWidth="1"
        strokeLinejoin="round"
      />
      {/* interlocked F + S monogram */}
      <g stroke={`url(#crest-${id})`} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M24 21 L24 44 M24 21 L37 21 M24 31.4 L33 31.4" />
        <path d="M43 24.2 C43 20.4 33.4 20.4 33.4 25.6 C33.4 30.2 43 30.4 43 35.4 C43 40.6 33.4 40.6 33.4 37" />
      </g>
    </svg>
  );
};

/**
 * Family Souq wordmark lockup — SOUQ is the dominant element (bold Fraunces),
 * with FAMILY as a spaced maison descriptor above it, and a crest to the left.
 * Props: size sm|md|lg, rule (brass hairline under SOUQ), withCrest, tone (kept for API compat).
 */
export const Wordmark = ({
  size = "md",
  tone = "light", // eslint-disable-line no-unused-vars
  rule = false,
  withCrest = true,
  className,
  "data-testid": testId,
  ...rest
}) => {
  const dims = {
    sm: { crest: 26, family: "text-[9px]", souq: "text-[18px]", gap: "gap-2.5", rule: "w-12", track: "0.32em" },
    md: { crest: 32, family: "text-[10px]", souq: "text-[22px]", gap: "gap-3", rule: "w-16", track: "0.34em" },
    lg: { crest: 44, family: "text-[12px]", souq: "text-[32px]", gap: "gap-3.5", rule: "w-24", track: "0.36em" },
  }[size];

  return (
    <div
      className={cn("group inline-flex select-none items-center", dims.gap, className)}
      data-testid={testId || "wordmark"}
      {...rest}
    >
      {withCrest && <CrestMark size={dims.crest} />}
      <span className="inline-flex flex-col leading-none">
        <span
          className={cn("font-display uppercase text-[color:var(--mk-text-2)]", dims.family)}
          style={{ letterSpacing: dims.track }}
        >
          Family
        </span>
        <span className={cn("font-display font-semibold leading-[1.02] tracking-[-0.01em] text-[color:var(--mk-text)]", dims.souq)}>
          Souq
        </span>
        {rule && <span className={cn("mk-brass-rule mt-1.5", dims.rule)} />}
      </span>
    </div>
  );
};
